import { useState } from 'react';
import { Tab } from '@headlessui/react';
import { UserCircleIcon, UsersIcon, CalendarIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

export default function Organization() {
  const [categories] = useState([
    {
      id: 'roles',
      name: 'Roles & Responsibilities',
      icon: UserCircleIcon,
      content: (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="space-y-6">
            <div className="border-b pb-6">
              <h3 className="text-lg font-medium">Cyber Crisis Executive Team (CCET)</h3>
              <p className="text-gray-600 mt-1">Strategic oversight and board communication</p>
              <ul className="mt-2 list-disc list-inside text-sm text-gray-600">
                <li>Operating Committee members</li>
                <li>Senior Executive-in-Charge (SEIC)</li>
                <li>Ensures adequate information flow to Board of Directors</li>
                <li>Strategic decision-making and oversight</li>
              </ul>
              <div className="mt-4">
                <button className="px-4 py-2 text-sm font-medium text-indigo-600 hover:text-indigo-500">
                  + Add CCET Member
                </button>
              </div>
            </div>

            <div className="border-b pb-6">
              <h3 className="text-lg font-medium">Cyber Crisis Management Team (CCMT)</h3>
              <p className="text-gray-600 mt-1">Tactical leadership and crisis management</p>
              <div className="mt-4 space-y-4">
                <div>
                  <h4 className="font-medium">Senior Executive-in-Charge (SEIC)</h4>
                  <ul className="mt-2 list-disc list-inside text-sm text-gray-600">
                    <li>Provides senior management guidance to EIC</li>
                    <li>Serves as escalation point for key decisions</li>
                    <li>Acts as liaison with CCET</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium">Executive-in-Charge (EIC)</h4>
                  <ul className="mt-2 list-disc list-inside text-sm text-gray-600">
                    <li>Full authority for tactical decision making</li>
                    <li>Oversees command-and-control activities</li>
                    <li>Directs crisis response operations</li>
                  </ul>
                </div>
              </div>
              <div className="mt-4">
                <button className="px-4 py-2 text-sm font-medium text-indigo-600 hover:text-indigo-500">
                  + Add CCMT Member
                </button>
              </div>
            </div>

            <div className="border-b pb-6">
              <h3 className="text-lg font-medium">Cyber Crisis Response Team (CCRT)</h3>
              <p className="text-gray-600 mt-1">Tactical response and incident handling</p>
              <div className="mt-4 space-y-4">
                <div>
                  <p className="text-sm text-gray-600 italic">Led by the Executive-in-Charge (EIC)</p>
                </div>
                <div>
                  <h4 className="font-medium">Lead Incident Handler (LIH)</h4>
                  <ul className="mt-2 list-disc list-inside text-sm text-gray-600">
                    <li>Responsible for incident's tactical coordination</li>
                    <li>Ensures incident response progression</li>
                    <li>Manages timely and accurate stakeholder communication</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium">Functional Group Incident Response Leads (IRL)</h4>
                  <p className="text-sm text-gray-600 mt-1">Responsibilities vary by functional group:</p>
                  <ul className="mt-2 list-disc list-inside text-sm text-gray-600">
                    <li>IT Security IRL: Security measures and technical response</li>
                    <li>Operations IRL: Business continuity and operational impact</li>
                    <li>Legal IRL: Regulatory compliance and legal implications</li>
                    <li>Communications IRL: Internal and external communications</li>
                  </ul>
                </div>
              </div>
              <div className="mt-4">
                <button className="px-4 py-2 text-sm font-medium text-indigo-600 hover:text-indigo-500">
                  + Add CCRT Member
                </button>
              </div>
            </div>

            <div className="border-b pb-6">
              <h3 className="text-lg font-medium">Cyber Crisis Support Team (CCST)</h3>
              <p className="text-gray-600 mt-1">Functional support for crisis management</p>
              <ul className="mt-2 list-disc list-inside text-sm text-gray-600">
                <li>Provides departmental expertise and resources</li>
                <li>Supports CCMT with specialized knowledge</li>
                <li>Facilitates cross-functional coordination</li>
                <li>Implements tactical response actions</li>
              </ul>
              <div className="mt-4">
                <button className="px-4 py-2 text-sm font-medium text-indigo-600 hover:text-indigo-500">
                  + Add CCST Member
                </button>
              </div>
            </div>

            <div className="border-b pb-6">
              <h3 className="text-lg font-medium">Computer Security Incident Response Team (CSIRT)</h3>
              <p className="text-gray-600 mt-1">Technical response execution</p>
              <ul className="mt-2 list-disc list-inside text-sm text-gray-600">
                <li>Coordinates technology-focused response activities</li>
                <li>Executes technical aspects of response plan</li>
                <li>Provides technical expertise and guidance</li>
                <li>Manages security tools and systems</li>
              </ul>
              <div className="mt-4">
                <button className="px-4 py-2 text-sm font-medium text-indigo-600 hover:text-indigo-500">
                  + Add CSIRT Member
                </button>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'contacts',
      name: 'Contacts',
      icon: UsersIcon,
      content: (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Call Sign</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap">Lebron Jordan</td>
                  <td className="px-6 py-4 whitespace-nowrap">GOAT 2</td>
                  <td className="px-6 py-4 whitespace-nowrap">Executive-in-Charge (EIC)</td>
                  <td className="px-6 py-4 whitespace-nowrap">+1 (888) 777-4444</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                      Available
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap">Michael James</td>
                  <td className="px-6 py-4 whitespace-nowrap">GOAT 1</td>
                  <td className="px-6 py-4 whitespace-nowrap">Senior Executive-in-Charge (SEIC)</td>
                  <td className="px-6 py-4 whitespace-nowrap">+1 (111) 111-1111</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                      Not Available
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap">Larry Hawk</td>
                  <td className="px-6 py-4 whitespace-nowrap">Sniper</td>
                  <td className="px-6 py-4 whitespace-nowrap">Lead Incident Handler (LIH)</td>
                  <td className="px-6 py-4 whitespace-nowrap">+1 (333) 757-4444</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                      Available
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap">Stephen Murry</td>
                  <td className="px-6 py-4 whitespace-nowrap">Overwatch</td>
                  <td className="px-6 py-4 whitespace-nowrap">CSIRT Team Lead</td>
                  <td className="px-6 py-4 whitespace-nowrap">+1 (415) 444-5555</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                      Available
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
            <div className="mt-4">
              <button className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
                Add Contact
              </button>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'irl-activities',
      name: 'Group IRL Activities',
      icon: CalendarIcon,
      content: (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="space-y-4">
            <div className="border-b pb-4">
              <h3 className="text-lg font-medium">Upcoming Activities</h3>
              <div className="mt-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Crisis Response Training</p>
                    <p className="text-sm text-gray-600">June 15, 2024 - Conference Room A</p>
                  </div>
                  <button className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
                    Register
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Team Building Workshop</p>
                    <p className="text-sm text-gray-600">July 1, 2024 - Main Hall</p>
                  </div>
                  <button className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
                    Register
                  </button>
                </div>
              </div>
              <div className="mt-4">
                <button className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
                  Add Activity
                </button>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'working-groups',
      name: 'Working Groups',
      icon: UserGroupIcon,
      content: (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="space-y-6">
            <div className="border-b pb-4">
              <h3 className="text-lg font-medium">Technical Response Team</h3>
              <p className="text-gray-600 mt-1">Handles technical aspects of incident response</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">5 Members</span>
                <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">Active</span>
              </div>
            </div>
            <div className="border-b pb-4">
              <h3 className="text-lg font-medium">Communications Team</h3>
              <p className="text-gray-600 mt-1">Manages internal and external communications</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">3 Members</span>
                <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">Active</span>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-medium">Legal & Compliance Team</h3>
              <p className="text-gray-600 mt-1">Ensures regulatory compliance and legal requirements</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">4 Members</span>
                <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">Active</span>
              </div>
            </div>
            <div className="mt-4">
              <button className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
                Create Working Group
              </button>
            </div>
          </div>
        </div>
      ),
    },
  ]);

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Organization Structure</h2>
      <Tab.Group>
        <Tab.List className="flex space-x-4 border-b border-gray-200">
          {categories.map((category) => (
            <Tab
              key={category.id}
              className={({ selected }) =>
                clsx(
                  'flex items-center space-x-2 py-2 px-4 text-sm font-medium border-b-2 outline-none',
                  selected
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                )
              }
            >
              <category.icon className="h-5 w-5" />
              <span>{category.name}</span>
            </Tab>
          ))}
        </Tab.List>
        <Tab.Panels>
          {categories.map((category) => (
            <Tab.Panel key={category.id}>{category.content}</Tab.Panel>
          ))}
        </Tab.Panels>
      </Tab.Group>
    </div>
  );
}